import {matchRoutes, useLocation} from "react-router-dom";
import type {AppRouteHandle, AppRouteObject, BreadcrumbParams} from "./types";

export type AppBreadcrumb = {
	label: string;
	href: string;
};

function resolveBreadcrumbLabel(
	handle: AppRouteHandle | undefined,
	params: BreadcrumbParams,
): string | undefined {
	const breadcrumb = handle?.breadcrumb;
	if (!breadcrumb) {
		return undefined;
	}
	return typeof breadcrumb === "function" ? breadcrumb(params) : breadcrumb;
}

export function getAppBreadcrumbs(
	routeConfig: AppRouteObject[],
	pathname: string,
): AppBreadcrumb[] {
	const matches = matchRoutes(routeConfig, pathname) ?? [];

	const crumbs = matches.flatMap((match) => {
		const label = resolveBreadcrumbLabel(
			match.route.handle as AppRouteHandle | undefined,
			match.params,
		);
		if (!label) {
			return [];
		}
		return [{label, href: match.pathname}];
	});

	return applyBreadcrumbTrail(pathname, crumbs);
}

function applyBreadcrumbTrail(pathname: string, crumbs: AppBreadcrumb[]): AppBreadcrumb[] {
	const productDetail = pathname.match(/^\/ozon\/products\/([^/]+)$/);
	if (productDetail) {
		const offerId = decodeURIComponent(productDetail[1]);
		return [
			{label: 'Ozon', href: '/ozon'},
			{label: 'Товар', href: '/ozon/products'},
			{label: offerId, href: pathname},
		];
	}

	const purchaseDetail = pathname.match(/^\/purchases\/([^/]+)$/);
	if (purchaseDetail) {
		const purchaseId = decodeURIComponent(purchaseDetail[1]);
		return [
			{label: 'Закупки', href: '/purchases'},
			{label: purchaseId, href: pathname},
		];
	}

	return crumbs;
}

export function useAppBreadcrumbs(routeConfig: AppRouteObject[]): AppBreadcrumb[] {
	const {pathname} = useLocation();
	return getAppBreadcrumbs(routeConfig, pathname);
}
